import type { IMetadata } from '@adloid-technologies/discover-sdk';

// ============================================================================
// Chevrolet Corvette — graphics metadata (runtime source consumed by the loader)
// Structure mirrors 2378 (Traverse). Single model "Corvette", single variant
// "coupe_3lt" (Stingray Coupe - 3LT). SKUs/identifiers sourced from the
// "Projects mapping_GU - Corvette" sheet. Project id for all Corvette assets: 59.
// ============================================================================
//
// Interior sub-option accessories (steering wheel, seat, seat belt, stitch) are
// generated below from a compact spec via `_intAcc`. They are modelled as
// single-select accessory groups (groupId) so the SDK applies them by id and
// deselects others in the same group. The interior cascade/conditional
// availability (which belt/stitch set shows for the selected seat, and default
// resets) is driven APP-SIDE from ui-metadata `config.interiorConfig` — the SDK
// is untouched, so other linkIds are unaffected. isDefault is intentionally
// false for all of these: the app applies the correct defaults explicitly.

// Variant groups (relevantVariants targeting). Listing a not-yet-added variant
// here is harmless — the SDK just filters by the active variantID.
const _STINGRAY = ['coupe_1lt', 'coupe_2lt', 'coupe_3lt', 'conv_2lt'];
const _GSX = ['gsx_coupe_3lz', 'gsx_conv_3lz'];
const _ALLV = ['coupe_1lt', 'coupe_2lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'];
const _NAPA = ['coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz']; // GT2/Sport Napa interiors (not 1LT). NOTE: coupe_2lt is not in this group (its other Napa colours are scoped individually and their seats/belts/stitch applied app-side on selection), but it defaults to Jet Black Napa, so the default seat/belt/stitch include it via _NAPA_DEFAULT_INT.
const _COUPE = ['coupe_1lt', 'coupe_2lt', 'coupe_3lt', 'gsx_coupe_3lz'];
const _COUPE_NAPA = ['coupe_3lt', 'gsx_coupe_3lz'];
const _CONV = ['conv_2lt', 'gsx_conv_3lz'];

// Convertible Napa interior colors: same 11 color keys as the coupe (identical
// names/seats) but on different interior-color SKUs. Keyed `<coupeKey>Conv` and
// spread into colors.interior below; interiorConfig reuses the shared seat ids.
const _CONV_INT_SKU: Record<string, string> = {
  intJetBlack: '6760',
  intSkyCoolGray: '6755',
  intNatural: '6713',
  intAdrenalineRed: '6740',
  intSantoriniBlue: '6694',
  intNaturalDipped: '6667',
  intAdrenalineRedDipped: '6749',
  intAsymmetricalAdrRed: '6680',
  intHabanero: '6661',
  intVeryDarkAtmosphere: '6735',
  intUltimateSuedeJetBlack: '6705',
};
const _convInteriorColors = Object.entries(_CONV_INT_SKU).reduce((acc, [k, sku]) => {
  acc[`${k}Conv`] = { sku, project: '59', relevantVariants: _CONV, notCompatibleExteriorColors: [] as string[] };
  return acc;
}, {} as Record<string, { sku: string; project: string; relevantVariants: string[]; notCompatibleExteriorColors: string[] }>);

// Interior parts (seat / steering / seat belt / stitch) are applied on the
// common_exterior asset (SKU 6659) — same identifier the interior interactions
// use in the source sheet. `isDefault: true` entries load at variant init so the
// default interior configuration appears with the car (and gates VARIANT_LOADED).
// Seat/belt/stitch/steering SKUs are shared across all Napa variants (_NAPA).
const _intAcc = (groupId: string, messages: string[], isDefault = false, rv: string[] = _NAPA) => ({
  view: 'interior' as const,
  groupId,
  display: true,
  isDefault,
  applyMessages: messages.map((message) => ({
    message,
    identifiers: ['common_exterior'],
    condition: '',
    isDefault: false,
    isStop: false,
  })),
  cameraMessages: [] as [],
  shaderMessages: [] as [],
  affectedInteractionOnApply: {},
  mesagesOnApply: [] as [],
  messagesOnRemove: [] as [],
  dependencyAccessories: [] as [],
  relevantVariants: rv,
  notAvailableForColor: { interior: [] as string[], exterior: [] as string[] },
});

// seat interaction names per interior color: [gt2Msg, sportMsg]
const _seatSpec: Record<string, [string, string]> = {
  intJetBlack: ['JetBlack_Napa_Leather_GT2', 'JetBlack_Napa_Leather_Sport'],
  intSkyCoolGray: ['Sky_Cool_Grey_Napa_Leather_GT2', 'Sky_Cool_Grey_Napa_Leather_Sport'],
  intNatural: ['Natural_Napa_Leather_GT2', 'Natural_Napa_Leather_Sport'],
  intAdrenalineRed: ['Adrenaline_Red_Napa_Leather_GT2', 'Adrenaline_Red_Napa_Leather_Sport'],
  intSantoriniBlue: ['Santorini_Blue_Napa_Leather_GT2', 'Santorini_Blue_Napa_Leather_Sport'],
  intNaturalDipped: ['Natural_Dipped_Napa_Leather_GT2', 'Natural_Dipped_Napa_Leather_Sport'],
  intAdrenalineRedDipped: ['Adrenaline_Red_Dipped_Napa_Leather_GT2', 'Adrenaline_Red_Dipped_Napa_Leather_Sport'],
  intAsymmetricalAdrRed: ['Asymmetrical_Adrenaline_Red_Napa_Leather_GT2', 'Asymmetrical_Adrenaline_Red_Napa_Leather_Sport'],
  intAsymSantoriniJetBlack: ['Asymmetrical_Adrenaline_Blue_Napa_Leather_GT2', 'Asymmetrical_Adrenaline_Blue_Napa_Leather_Sport'],
  intHabanero: ['Habenera_Napa_Leather_GT2', 'Habenera_Napa_Leather_Sport'],
  intVeryDarkAtmosphere: ['Very_Dark_Atmosphere_Napa_Leather_GT2', 'Very_Dark_Atmosphere_Napa_Leather_Sport'],
  intUltimateSuedeJetBlack: ['Ultimate_Suede_JetBlack_Napa_Leather_GT2', 'Ultimate_Suede_JetBlack_Napa_Leather_Sport'],
};

// The default interior color is Jet Black (interior-color SKU 6738), whose
// default seat is GT2 — mark it isDefault so it loads with the variant.
const _DEFAULT_SEAT_KEY = 'seat_intJetBlack_gt2';
const _seatAccessories = Object.entries(_seatSpec).reduce((acc, [colorKey, [gt2, sport]]) => {
  acc[`seat_${colorKey}_gt2`] = _intAcc('Seat', [gt2], `seat_${colorKey}_gt2` === _DEFAULT_SEAT_KEY);
  acc[`seat_${colorKey}_sport`] = _intAcc('Seat', [sport]);
  return acc;
}, {} as Record<string, ReturnType<typeof _intAcc>>);
// coupe_2lt also defaults to Jet Black Napa (see its defaultSkus), so the default
// GT2 seat/belt/stitch must load there too. New array: _NAPA itself stays as is
// (it also scopes non-interior defaults such as accZ51None).
const _NAPA_DEFAULT_INT = ['coupe_2lt', ..._NAPA];
_seatAccessories[_DEFAULT_SEAT_KEY].relevantVariants = _NAPA_DEFAULT_INT;
// Asymmetrical Santorini Blue / Jet Black has a THIRD seat (Sport GT2) beyond the
// standard GT2/Sport pair — added manually (message per the source sheet).
_seatAccessories['seat_intAsymSantoriniJetBlack_sport_gt2'] = _intAcc('Seat', ['Asymmetrical_Adrenaline_Blue_Napa_Leather_Sport_GT2']);

// Sueded-microfiber seat variants ([gt2SuededMsg, sportSuededMsg]). Only 8 colors
// have a sueded option (Santorini / Adrenaline Red Dipped / Asymmetrical do not).
// Surfaced as separate "(sueded microfiber)" interior-color cards below.
const _suededSeatSpec: Record<string, [string, string]> = {
  intJetBlack: ['JetBlack_Napa_Leather_GT2_Sueded', 'JetBlack_Napa_Leather_Sport_Sueded'],
  intSkyCoolGray: ['Sky_Cool_Grey_Napa_Leather_GT2_Sueded', 'Sky_Cool_Grey_Napa_Leather_Sport_Sueded'],
  intNatural: ['Natural_Napa_Leather_GT2_Sueded', 'Natural_Napa_Leather_Sport_Sueded'],
  intAdrenalineRed: ['Adrenaline_Red_Napa_Leather_GT2_Sueded', 'Adrenaline_Red_Napa_Leather_Sport_Sueded'],
  intNaturalDipped: ['Natural_Dipped_Napa_Leather_GT2_Sueded', 'Natural_Dipped_Napa_Leather_Sport_Sueded'],
  intHabanero: ['Habenera_Napa_Leather_GT2_Sueded', 'Habenera_Napa_Leather_Sport_Sueded'],
  intVeryDarkAtmosphere: ['Very_Dark_Napa_Leather_GT2_Sueded', 'Very_Dark_Napa_Leather_Sport_Sueded'],
  intUltimateSuedeJetBlack: ['Ultimate_Suede_Napa_Leather_GT2_Sueded', 'Ultimate_Suede_Napa_Leather_Sport_Sueded'],
};
const _suededSeatAccessories = Object.entries(_suededSeatSpec).reduce((acc, [colorKey, [gt2, sport]]) => {
  acc[`seat_${colorKey}_gt2_sueded`] = _intAcc('Seat', [gt2]);
  acc[`seat_${colorKey}_sport_sueded`] = _intAcc('Seat', [sport]);
  return acc;
}, {} as Record<string, ReturnType<typeof _intAcc>>);
// Coupe interior-color SKUs (perforated) for the 8 colors that have a sueded card.
const _COUPE_INT_SKU: Record<string, string> = {
  intJetBlack: '6738', intSkyCoolGray: '6647', intNatural: '6708', intAdrenalineRed: '6665',
  intNaturalDipped: '6643', intHabanero: '6684', intVeryDarkAtmosphere: '6725', intUltimateSuedeJetBlack: '6697',
};

const _interiorSubAccessories = {
  // steering wheel (default loads at init) — shared across ALL variants incl. 1LT
  steering_default: _intAcc('SteeringWheel', ['Steering_wheel_Default'], true, _ALLV),
  steering_sueded: _intAcc('SteeringWheel', ['Steering_wheel_Sueded_Microfiber'], false, _ALLV),
  // seat belts — GT1 set (Stingray Coupe-1LT / Mulan interiors). NOT isDefault:
  // the Mulan interior-color asset already loads the default black belt, and
  // gating VARIANT_LOADED on a GT1 belt ack was hanging the 1LT load. The cascade
  // still (re)applies the default belt after load; belt switching is unaffected.
  seatbelt_gt1_black: _intAcc('SeatBelt', ['SeatBelt_Black_GT1'], false, ['coupe_1lt', 'coupe_2lt']),
  seatbelt_gt1_orange: _intAcc('SeatBelt', ['SeatBelt_Orange_GT1'], false, ['coupe_1lt', 'coupe_2lt']),
  seatbelt_gt1_natural: _intAcc('SeatBelt', ['SeatBelt_Natural_GT1'], false, ['coupe_1lt', 'coupe_2lt']),
  seatbelt_gt1_blue: _intAcc('SeatBelt', ['SeatBelt_Blue_GT1'], false, ['coupe_1lt', 'coupe_2lt']),
  seatbelt_gt1_red: _intAcc('SeatBelt', ['SeatBelt_Red_GT1'], false, ['coupe_1lt', 'coupe_2lt']),
  seatbelt_gt1_yellow: _intAcc('SeatBelt', ['SeatBelt_Yellow_GT1'], false, ['coupe_1lt', 'coupe_2lt']),
  // seat belts — GT2 set (black is the default → loads at init)
  seatbelt_gt2_black: _intAcc('SeatBelt', ['SeatBelt_Black_GT2'], true, _NAPA_DEFAULT_INT),
  seatbelt_gt2_orange: _intAcc('SeatBelt', ['SeatBelt_Orange_GT2']),
  seatbelt_gt2_natural: _intAcc('SeatBelt', ['SeatBelt_Natural_GT2']),
  seatbelt_gt2_blue: _intAcc('SeatBelt', ['SeatBelt_Blue_GT2']),
  seatbelt_gt2_red: _intAcc('SeatBelt', ['SeatBelt_Red_GT2']),
  seatbelt_gt2_yellow: _intAcc('SeatBelt', ['SeatBelt_Yellow_GT2']),
  // seat belts — Sport set. Relevant for _ALLV (Napa Sport seats + the 1LT
  // Jet Black Performance textile interior, which uses this Sport belt set).
  seatbelt_sport_black: _intAcc('SeatBelt', ['SeatBelt_Black_Sport'], false, _ALLV),
  seatbelt_sport_orange: _intAcc('SeatBelt', ['SeatBelt_Orange_Sport'], false, _ALLV),
  seatbelt_sport_natural: _intAcc('SeatBelt', ['SeatBelt_Natural_Sport'], false, _ALLV),
  seatbelt_sport_blue: _intAcc('SeatBelt', ['SeatBelt_Blue_Sport'], false, _ALLV),
  seatbelt_sport_red: _intAcc('SeatBelt', ['SeatBelt_Red_Sport'], false, _ALLV),
  seatbelt_sport_yellow: _intAcc('SeatBelt', ['SeatBelt_Yellow_Sport'], false, _ALLV),
  // stitch — GT2 set (base stitch + seat-specific stitch); white is the default → loads at init
  stitch_gt2_white: _intAcc('Stitch', ['Stitch_White', 'Stitch_White_GT2'], true, _NAPA_DEFAULT_INT),
  stitch_gt2_blue: _intAcc('Stitch', ['Stitch_Blue', 'Stitch_Blue_GT2']),
  stitch_gt2_red: _intAcc('Stitch', ['Stitch_Red', 'Stitch_Red_GT2']),
  stitch_gt2_yellow: _intAcc('Stitch', ['Stitch_Yellow', 'Stitch_Yellow_GT2']),
  // stitch — Sport set
  stitch_sport_white: _intAcc('Stitch', ['Stitch_White', 'Stitch_White_Sport']),
  stitch_sport_blue: _intAcc('Stitch', ['Stitch_Blue', 'Stitch_Blue_Sport']),
  stitch_sport_red: _intAcc('Stitch', ['Stitch_Red', 'Stitch_Red_Sport']),
  stitch_sport_yellow: _intAcc('Stitch', ['Stitch_Yellow', 'Stitch_Yellow_Sport']),
  ..._seatAccessories,
  ..._suededSeatAccessories,
};

// Sueded-microfiber interior-color cards: reuse the perforated color asset SKU
// (coupe or convertible) but drive sueded seats + sueded steering via interiorConfig.
const _suededInteriorColors = Object.keys(_suededSeatSpec).reduce((acc, k) => {
  acc[`${k}Sueded`] = { sku: _COUPE_INT_SKU[k], project: '59', relevantVariants: _COUPE_NAPA, notCompatibleExteriorColors: [] as string[] };
  acc[`${k}SuededConv`] = { sku: _CONV_INT_SKU[k], project: '59', relevantVariants: _CONV, notCompatibleExteriorColors: [] as string[] };
  return acc;
}, {} as Record<string, { sku: string; project: string; relevantVariants: string[]; notCompatibleExteriorColors: string[] }>);

// Stingray Coupe-2LT is offered the Napa sueded colours incrementally (only the ones
// supplied so far). Each gets its OWN rv array so we don't mutate the shared _COUPE_NAPA
// reference used by every other sueded card.
_suededInteriorColors['intJetBlackSueded'].relevantVariants = ['coupe_2lt', ..._COUPE_NAPA];
_suededInteriorColors['intSkyCoolGraySueded'].relevantVariants = ['coupe_2lt', ..._COUPE_NAPA];
_suededInteriorColors['intNaturalSueded'].relevantVariants = ['coupe_2lt', ..._COUPE_NAPA];
_suededInteriorColors['intAdrenalineRedSueded'].relevantVariants = ['coupe_2lt', ..._COUPE_NAPA];

// Exterior parts (calipers/roof/engine/exhaust/orvm/spoiler/alloy/graphics) apply
// on the common_exterior asset. Helper mirrors _intAcc but with view 'exterior'.
const _extAcc = (groupId: string, messages: string[], isDefault = false, rv: string[] = _STINGRAY, identifier = 'common_exterior', display = true, cameraMessage = '') => ({
  view: 'exterior' as const,
  groupId,
  display,
  isDefault,
  applyMessages: messages.map((message) => ({
    message,
    identifiers: [identifier],
    condition: '',
    isDefault: false,
    isStop: false,
  })),
  // A camera message (e.g. CAMERA_ALLOY) focuses the camera on the part on apply and,
  // via the SDK's AccessoryStore subscription, brings up the Free Roam view.
  cameraMessages: (cameraMessage
    ? [{ message: cameraMessage, identifiers: [identifier], condition: '', isDefault: false, isStop: false }]
    : []) as any[],
  shaderMessages: [] as [],
  affectedInteractionOnApply: {},
  mesagesOnApply: [] as [],
  messagesOnRemove: [] as [],
  dependencyAccessories: [] as [],
  relevantVariants: rv,
  notAvailableForColor: { interior: [] as string[], exterior: [] as string[] },
});

// Graphics/stripe accessory: like _extAcc('Graphics', ...) but also closes the boot
// and the roof/sunroof on apply (affectedInteractionOnApply), so the decal doesn't
// float when the boot or roof is open.
const _graphicsAcc = (message: string, rv: string[]) => {
  // CAMERA_GRAPHICS gives the accessory a camera so the SDK's AccessoryStore subscription
  // flips accessoryView to 'free-roam' — i.e. the Free Roam / Featured View toggle appears
  // (it only shows when the active accessory has cameraMessages).
  const a = _extAcc('Graphics', [message], false, rv, 'common_exterior', true, 'CAMERA_GRAPHICS');
  a.affectedInteractionOnApply = { intrBoot: { add: false }, intrRoof: { add: false } };
  return a;
};

// Convertible roof accessory (roof material swap). Reloading the roof asset closes the
// roof in graphics, so also reset the intrRoof interaction to closed — otherwise the
// roof-open toggle in the interaction UI stays "open" while the roof is actually shut.
const _convRoofAcc = (message: string, isDefault = false, display = true) => {
  const a = _extAcc('Roof', [message], isDefault, _CONV, 'exterior-color', display);
  a.affectedInteractionOnApply = { intrRoof: { add: false } };
  return a;
};

// Showcase feature. Mirrors the working 2378 layout: a `cinematic` message
// (the "Interaction Name/Cinematics" from the sheet) with waitForFulfill:true that
// PACES the autoplay loop — HandleFeaturesAutoplay awaits GetCinematicDuration + the
// cinematic on it, which yields to the event loop each iteration. WITHOUT a
// waitForFulfill message the loop never yields (empty webgl promises resolve as
// microtasks) and the browser freezes. `shader` is the optional SHADER_* glow
// (waitForFulfill:false, fire-and-forget). `rv` = trims it applies to.
const _feat = (view: 'exterior' | 'interior', rv: string[], cinematic: string, shader = '', affectedInteractionOnApply: Record<string, { add: boolean; snapToLastFrame?: boolean; withoutCamera?: boolean }> = {}) => ({
  type: 'graphic' as const,
  view,
  relevantVariants: rv,
  messages: [
    ...(shader ? [{ message: shader, identifiers: ['common_exterior'], waitForFulfill: false, cascade: false }] : []),
    { message: cinematic, identifiers: ['common_exterior'], waitForFulfill: true, cascade: false },
  ] as any[],
  mesagesOnApply: [] as [],
  messagesOnRemove: [] as [],
  affectedInteractionOnApply: affectedInteractionOnApply as any,
});

// Stingray Coupe graphics/stripes (all optional; no default). [interactionName, sku]
const _graphicsSpec: Array<[string, string]> = [
  ['Asymmetrical_Adrenaline_Red_Carbon_Flash_Coupe_Stin', '6821'],
  ['Carbon_Flash_Blue_Accent_Coupe_Stin', '6843'],
  ['Carbon_Flash_Yellow_Accent_Coupe_Stin', '6842'],
  ['Carbon_Flash_Orange_Accent_Coupe_Stin', '6847'],
  ['Carbon_Flash_Red_Accent_Coupe_Stin', '6832'],
  ['Carbon_Flash_Sliver_Accent_Coupe_Stin', '6838'],
  ['Carbon_Flash_Metallic_Coupe_Stin', '6824'],
  ['Edge_Orange_Coupe_Stin', '6839'],
  ['Edge_Red_Coupe_Stin', '6835'],
  ['Sterling_Silver_Coupe_Stin', '6845'],
  ['Competition_Yellow_Coupe_Stin', '6827'],
  ['Carbon_Flash_Edge_Red_Stin', '6823'],
  ['Carbon_Flash_Competition_Yellow_Sting', '6831'],
  ['Carbon_Flash_Midnight_Silver_Sting', '6829'],
  ['Asymmetrical_Adrenaline_Blue_Carbon_Flash_Coupe_Stin', '6857'],
];
// Stingray COUPE graphics → coupe_1lt + coupe_3lt only (convertible & GS have their own sets).
const _graphicsAccessories = _graphicsSpec.reduce((acc, [msg, sku]) => {
  acc[`graphics_${sku}`] = _graphicsAcc(msg, ['coupe_1lt', 'coupe_2lt', 'coupe_3lt']);
  return acc;
}, {} as Record<string, ReturnType<typeof _extAcc>>);

// ── GS X exterior accessories (Grand Sport) ──────────────────────────────────
const _gsCoupeGraphicsSpec: Array<[string, string]> = [
  ['Asymmetrical_Adrenaline_Red_Carbon_Flash_Coupe_GS', '6698'],
  ['Asymmetrical_Adrenaline_Blue_Carbon_Flash_Coupe_GS', '7003'],
  ['Carbon_Flash_Blue_Accent_Coupe_GS', '6795'],
  ['Carbon_Flash_Yellow_Accent_Coupe_GS', '6707'],
  ['Carbon_Flash_Orange_Accent_Coupe_GS', '6785'],
  ['Carbon_Flash_Red_Accent_Coupe_GS', '6717'],
  ['Carbon_Flash_Sliver_Accent_Coupe_GS', '6695'],
  ['Carbon_Flash_Metallic_Coupe_GS', '6703'],
  ['Edge_Orange_Coupe_GS', '6721'],
  ['Edge_Red_Coupe_GS', '6787'],
  ['Sterling_Silver_Coupe_GS', '6786'],
  ['Competition_Yellow_Coupe_GS', '6723'],
  ['Carbon_Flash_Edge_Red_GS', '6714'],
  ['Carbon_Flash_Competition_Yellow_GS', '6747'],
  ['Carbon_Flash_Midnight_Silver_GS', '6741'],
];
const _gsAccessories: Record<string, ReturnType<typeof _extAcc>> = {
  // GS calipers (Dark Gray Metallic is baseline)
  gsCaliperDarkGray: _extAcc('Caliper', ['Calipers_Dark_Gray_Metallic_GS'], true, _GSX, 'common_exterior', false),
  gsCaliperBrightRed: _extAcc('Caliper', ['Calipers_Bright_Red_GS'], false, _GSX),
  gsCaliperEdgeRed: _extAcc('Caliper', ['Calipers_Edge_Red_GS'], false, _GSX),
  gsCaliperVelocityYellow: _extAcc('Caliper', ['Calipers_Velocity_Yellow_GS'], false, _GSX),
  gsCaliperOrange: _extAcc('Caliper', ['Calipers_Orange_GS'], false, _GSX),
  // GS alloys — NOT isDefault: the alloy defaultSku already loads the default
  // wheel, so an isDefault interaction piled a second alloy on top.
  gsAlloyROU: _extAcc('Alloy Wheels', ['ROU'], false, _GSX, 'common_exterior', false, 'CAMERA_ALLOY'),
  gsAlloyROX: _extAcc('Alloy Wheels', ['ROX'], false, _GSX, 'common_exterior', true, 'CAMERA_ALLOY'),
  gsAlloySOM: _extAcc('Alloy Wheels', ['SOM'], false, _GSX, 'common_exterior', true, 'CAMERA_ALLOY'),
  gsAlloySON: _extAcc('Alloy Wheels', ['SON'], false, _GSX, 'common_exterior', true, 'CAMERA_ALLOY'),
  gsAlloyROY: _extAcc('Alloy Wheels', ['ROY'], false, _GSX, 'common_exterior', true, 'CAMERA_ALLOY'),
  gsAlloyROZ: _extAcc('Alloy Wheels', ['ROZ'], false, _GSX, 'common_exterior', true, 'CAMERA_ALLOY'),
  gsAlloySTZ: _extAcc('Alloy Wheels', ['STZ'], false, _GSX, 'common_exterior', true, 'CAMERA_ALLOY'),
  // GS "Carbon Flash Metallic mirrors and spoiler" is ONE combined package
  // (gsCarbonFlashDefault / gsCarbonFlashPkg literals below) — bundles ORVM + spoiler.
  // GS ground effects pair: none default (hidden) + carbon fiber/flash optional (shown).
  gsGroundEffectsNone: _extAcc('GroundEffectsPkg', ['WO_Ground_Effects'], true, _GSX, 'common_exterior', false),
  gsGroundEffectsCarbonFiber: _extAcc('GroundEffectsPkg', ['Ground_Effects_Carbon_Fiber'], false, _GSX),
  gsGroundEffectsCarbonFlash: _extAcc('GroundEffectsPkg', ['Ground_Effects_Carbon_Flash'], false, _GSX),
  // GS logo pair: black default (hidden, loads) + chrome optional (shown). Group 'Logo'.
  gsBadgeBlackCoupe: _extAcc('Logo', ['GS_Coupe_Logo_Black'], true, ['gsx_coupe_3lz'], 'common_exterior', false),
  gsBadgeChromeCoupe: _extAcc('Logo', ['GS_Coupe_Logo_Chrome'], false, ['gsx_coupe_3lz']),
  gsBadgeBlackConv: _extAcc('Logo', ['GS_Convertible_Logo_Black'], true, ['gsx_conv_3lz'], 'common_exterior', false),
  gsBadgeChromeConv: _extAcc('Logo', ['GS_Convertible_Logo_Chrome'], false, ['gsx_conv_3lz']),
  // GS body-accent pair: default (hidden) added in _baseStateAccessories; optional (shown) here.
  gsBodyColorAccent: _extAcc('BodyAccentPkg', ['GS_Exterior_Accent_Body_Color_Optional'], false, _GSX, 'exterior-color'),
};
_gsCoupeGraphicsSpec.forEach(([msg, sku]) => {
  _gsAccessories[`gsGraphics_${sku}`] = _graphicsAcc(msg, ['gsx_coupe_3lz']);
});
// GS X Grand Sport Heritage Hash Marks (coupe) — [message, sku, notAvailableExteriorColorIds].
// The listed exterior colours hide the graphic in the UI and, if applied while active, remove it
// (SDK conflict system via notAvailableForColor). Red Mist Metallic Tintcoat = extCrimsonVelocityRed.
const _gsHeritageCoupeSpec: Array<[string, string, string[]]> = [
  ['Admiral_Blue_GS_Coupe', '7013', ['extAdmiralBlue']],
  ['Carbon_Flash_GS_Coupe', '7009', []],
  ['Red_Mist_GS_Coupe', '7002', ['extTorchRed', 'extCrimsonVelocityRed']],
  ['Torch_Red_GS_Coupe', '7011', ['extTorchRed', 'extCrimsonVelocityRed']],
  ['Blade_Silver_GS_Coupe', '6996', ['extBladeSilver']],
  ['Competition_Yellow_GS_Coupe', '6991', ['extCompetitionYellow']],
];
_gsHeritageCoupeSpec.forEach(([msg, sku, naColors]) => {
  const a = _graphicsAcc(msg, ['gsx_coupe_3lz']);
  a.notAvailableForColor.exterior = naColors;
  _gsAccessories[`gsGraphics_${sku}`] = a;
});

// ── Convertible exterior accessories (Stingray Convertible-2LT + GS X Convertible-3LZ) ──
// Roofs are colorbody → exterior-color. Graphics/packages → common_exterior. GS convertible
// badge + GS spoiler/calipers/alloys/ground-effects already come from _gsAccessories (rv _GSX).
const _convStinGraphicsSpec: Array<[string, string]> = [
  ['Asymmetrical_Adrenaline_Red_Carbon_Flash_Stin', '6841'],
  ['Asymmetrical_Adrenaline_Blue_Carbon_Flash_Stin', '6867'],
  ['Carbon_Flash_Blue_Accent_Stin', '6834'],
  ['Carbon_Flash_Yellow_Accent_Stin', '6840'],
  ['Carbon_Flash_Orange_Accent_Stin', '6833'],
  ['Carbon_Flash_Red_Accent_Stin', '6844'],
  ['Carbon_Flash_Sliver_Accent_Stin', '6826'],
  ['Carbon_Flash_Metallic_Stin', '6828'],
  ['Edge_Orange_Stin', '6848'],
  ['Edge_Red_Stin', '6822'],
  ['Sterling_Silver_Stin', '6836'],
  ['Competition_Yellow_Stin', '6825'],
  ['Carbon_Flash_Edge_Red_Stin', '6823'],
  ['Carbon_Flash_Competition_Yellow_Sting', '6831'],
  ['Carbon_Flash_Midnight_Silver_Sting', '6829'],
];
const _convGsGraphicsSpec: Array<[string, string]> = [
  ['Asymmetrical_Adrenaline_Red_Carbon_Flash_Conv_GS', '6812'],
  ['Asymmetrical_Adrenaline_Blue_Carbon_Flash_Conv_GS', '6995'],
  ['Carbon_Flash_Blue_Accent_Conv_GS', '6808'],
  ['Carbon_Flash_Yellow_Accent _Conv_GS', '6805'],
  ['Carbon_Flash_Orange_Accent_Conv_GS', '6806'],
  ['Carbon_Flash_Red_Accent_Conv_GS', '6811'],
  ['Carbon_Flash_Sliver_Accent_Conv_GS', '6816'],
  ['Carbon_Flash_Metallic_Conv_GS', '6818'],
  ['Edge_Orange_Conv_GS', '6819'],
  ['Edge_Red_Conv_GS', '6809'],
  ['Sterling_Silver_Conv_GS', '6817'],
  ['Competition_Yellow_Conv_GS', '6820'],
];
const _convAccessories: Record<string, ReturnType<typeof _extAcc>> = {
  // Convertible roofs (colorbody → exterior-color)
  convRoofStandard: _convRoofAcc('Standard_Roof_Convertible', true, false),
  convRoofCarbonFlash: _convRoofAcc('Carbon_Flash_Roof_Convertible'),
  convRoofCarbonFlashBody: _convRoofAcc('Carbon_Flash_body_color_Roof_Convertible'),
  // Convertible option packages (single-select group Packages)
  convPkgStealthTrim: _extAcc('InteriorTrim', ['Stealth_Interior_Trim_Package_Convertible'], false, _CONV),
  convPkgCarbonFiberTrim: _extAcc('InteriorTrim', ['Carbon_Fiber_Interior_Trim_Convertible'], false, _CONV),
  convPkgEngineAppearance: _extAcc('EngineAppearancePkg', ['Convertible_Engine_Appearance_Optional'], false, _CONV),
  // Stingray convertible chrome badge (GS convertible badge already in _gsAccessories)
  convBadgeBlackStingray: _extAcc('Logo', ['StingRay_Convertible_Logo_Black'], true, ['conv_2lt'], 'common_exterior', false),
  convBadgeChromeStingray: _extAcc('Logo', ['StingRay_Convertible_Logo_Chrome'], false, ['conv_2lt']),
};
_convStinGraphicsSpec.forEach(([msg, sku]) => {
  _convAccessories[`convGraphics_${sku}`] = _graphicsAcc(msg, ['conv_2lt']);
});
_convGsGraphicsSpec.forEach(([msg, sku]) => {
  _convAccessories[`convGraphics_${sku}`] = _graphicsAcc(msg, ['gsx_conv_3lz']);
});
// GS X Grand Sport Heritage Hash Marks (convertible) — [message, sku, notAvailableExteriorColorIds].
const _gsHeritageConvSpec: Array<[string, string, string[]]> = [
  ['Admiral_Blue_GS_Convertible', '7016', ['extAdmiralBlue']],
  ['Carbon_Flash_GS_Convertible', '7019', []],
  ['Red_Mist_GS_Convertible', '7014', ['extTorchRed', 'extCrimsonVelocityRed']],
  ['Torch_Red_GS_Convertible', '7018', ['extTorchRed', 'extCrimsonVelocityRed']],
  ['Blade_Silver_GS_Convertible', '7017', ['extBladeSilver']],
  ['Competition_Yellow_GS_Convertible', '7015', ['extCompetitionYellow']],
];
_gsHeritageConvSpec.forEach(([msg, sku, naColors]) => {
  const a = _graphicsAcc(msg, ['gsx_conv_3lz']);
  a.notAvailableForColor.exterior = naColors;
  _convAccessories[`convGraphics_${sku}`] = a;
});
// Exterior colours each GS full-length / stinger graphic is NOT available with (hidden in
// UI + auto-removed on colour apply via the SDK conflict system). SKU → incompatible colour
// ids; keyed by SKU (coupe gsGraphics_* and convertible convGraphics_* SKUs don't overlap).
// Red Mist Metallic Tintcoat = extCrimsonVelocityRed.
const _gsGraphicsNotAvail: Record<string, string[]> = {
  // GS coupe full-length dual racing stripes
  '6698': ['extTorchRed', 'extCrimsonVelocityRed'],
  '7003': ['extAdmiralBlue'],
  '6795': ['extAdmiralBlue'],
  '6707': ['extCompetitionYellow'],
  '6785': ['extSebringOrange'],
  '6717': ['extTorchRed', 'extCrimsonVelocityRed'],
  '6721': ['extSebringOrange'],
  '6787': ['extTorchRed', 'extCrimsonVelocityRed'],
  '6723': ['extCompetitionYellow'],
  // GS coupe stinger stripes
  '6714': ['extTorchRed', 'extCrimsonVelocityRed'],
  '6747': ['extCompetitionYellow'],
  // GS convertible full-length dual racing stripes
  '6812': ['extTorchRed', 'extCrimsonVelocityRed'],
  '6995': ['extAdmiralBlue'],
  '6808': ['extAdmiralBlue'],
  '6805': ['extCompetitionYellow'],
  '6806': ['extSebringOrange'],
  '6811': ['extTorchRed', 'extCrimsonVelocityRed'],
  '6819': ['extSebringOrange'],
  '6809': ['extTorchRed', 'extCrimsonVelocityRed'],
  '6820': ['extCompetitionYellow'],
};
Object.entries(_gsGraphicsNotAvail).forEach(([sku, cols]) => {
  const acc = _gsAccessories[`gsGraphics_${sku}`] ?? _convAccessories[`convGraphics_${sku}`];
  if (acc) acc.notAvailableForColor.exterior = cols;
});
// Stingray full-length / stinger graphics — exterior colours each is NOT available with
// (hidden in UI + auto-removed on colour apply). Coupe SKUs live in _graphicsAccessories
// (graphics_*), convertible in _convAccessories (convGraphics_*); the 3 stinger SKUs
// (6823/6831/6829) exist in BOTH, so set both. Red Mist Metallic Tintcoat = extCrimsonVelocityRed.
const _stinGraphicsNotAvail: Record<string, string[]> = {
  // Stingray coupe full-length
  '6821': ['extTorchRed', 'extCrimsonVelocityRed'],
  '6857': ['extAdmiralBlue'],
  '6843': ['extAdmiralBlue'],
  '6842': ['extCompetitionYellow'],
  '6847': ['extSebringOrange'],
  '6832': ['extTorchRed', 'extCrimsonVelocityRed'],
  '6839': ['extSebringOrange'],
  '6835': ['extTorchRed', 'extCrimsonVelocityRed'],
  '6827': ['extCompetitionYellow'],
  // Stingray convertible full-length
  '6841': ['extTorchRed', 'extCrimsonVelocityRed'],
  '6867': ['extAdmiralBlue'],
  '6834': ['extAdmiralBlue'],
  '6840': ['extCompetitionYellow'],
  '6833': ['extSebringOrange'],
  '6844': ['extTorchRed', 'extCrimsonVelocityRed'],
  '6848': ['extSebringOrange'],
  '6822': ['extTorchRed', 'extCrimsonVelocityRed'],
  '6825': ['extCompetitionYellow'],
  // Stingray stinger stripes (shared coupe + convertible)
  '6823': ['extTorchRed', 'extCrimsonVelocityRed'],
  '6831': ['extCompetitionYellow'],
};
Object.entries(_stinGraphicsNotAvail).forEach(([sku, cols]) => {
  if (_graphicsAccessories[`graphics_${sku}`]) _graphicsAccessories[`graphics_${sku}`].notAvailableForColor.exterior = cols;
  if (_convAccessories[`convGraphics_${sku}`]) _convAccessories[`convGraphics_${sku}`].notAvailableForColor.exterior = cols;
});

// Base-state defaults from the sheet's "Default Interaction Name" column that are
// the standard side of a default/optional pair (the optional lives in Packages).
// Applied at load (isDefault) but hidden (display:false, own group) so they set
// the correct standard look without adding UI cards or clashing with Packages.
const _baseStateAccessories: Record<string, ReturnType<typeof _extAcc>> = {
  // Each default is paired (same groupId) with its optional package so applying the
  // optional removes the default. Hidden (display:false) + isDefault (loads at init).
  accBodyAccentDefaultStingray: _extAcc('BodyAccentPkg', ['StingRay_Exterior_Accent_Body_Color_Default'], true, _STINGRAY, 'exterior-color', false),
  accBodyAccentDefaultGs: _extAcc('BodyAccentPkg', ['GS_Exterior_Accent_Body_Color_Default'], true, _GSX, 'exterior-color', false),
  convEngineAppearanceDefault: _extAcc('EngineAppearancePkg', ['Convertible_Engine_Appearance_Default'], true, _CONV, 'common_exterior', false),
  // Standard-suspension baseline: WO_E60 (no Z51), hidden, loads at init for Napa variants.
  // The selectable Z51 Magnetic Ride package (accZ51Suspension / W_E60) has been removed
  // from all variants, so this is now the only member of Z51Pkg (kept so the car still
  // explicitly loads in standard-suspension state).
  accZ51None: _extAcc('Z51Pkg', ['WO_E60'], true, _NAPA, 'common_exterior', false),
};

export const metadata: IMetadata = {
  models: {
    Corvette: {
      id: 'Corvette',
      lightingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      postProcessingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      backgroundIdentifer: 'dome',
      variants: {
        coupe_3lt: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '6659',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '6645',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '6738',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'variants_parts',
              sku: '6646',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'alloy',
              sku: '6789',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '6969',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        gsx_coupe_3lz: {
          defaultSkus: [
            { id: 'common_exterior', sku: '6659', projectId: '59', position: [0, 0, 0] },
            { id: 'exterior-color', sku: '6645', projectId: '59', position: [0, 0, 0] },
            { id: 'interior-color', sku: '6738', projectId: '59', position: [0, 0, 0] },
            { id: 'variants_parts', sku: '6693', projectId: '59', position: [0, 0, 0] },
            { id: 'alloy', sku: '6712', projectId: '59', position: [0, 0, 0] },
            { id: 'dome', sku: '6970', projectId: '59', position: [0, 0, 0], maxResolution: { ios: '2K', android: '4K' } },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        conv_2lt: {
          defaultSkus: [
            { id: 'common_exterior', sku: '6659', projectId: '59', position: [0, 0, 0] },
            { id: 'exterior-color', sku: '6645', projectId: '59', position: [0, 0, 0] },
            { id: 'interior-color', sku: '6760', projectId: '59', position: [0, 0, 0] },
            { id: 'variants_parts', sku: '6699', projectId: '59', position: [0, 0, 0] },
            { id: 'alloy', sku: '6789', projectId: '59', position: [0, 0, 0] },
            { id: 'dome', sku: '6969', projectId: '59', position: [0, 0, 0], maxResolution: { ios: '2K', android: '4K' } },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        gsx_conv_3lz: {
          defaultSkus: [
            { id: 'common_exterior', sku: '6659', projectId: '59', position: [0, 0, 0] },
            { id: 'exterior-color', sku: '6645', projectId: '59', position: [0, 0, 0] },
            { id: 'interior-color', sku: '6760', projectId: '59', position: [0, 0, 0] },
            { id: 'variants_parts', sku: '6649', projectId: '59', position: [0, 0, 0] },
            { id: 'alloy', sku: '6712', projectId: '59', position: [0, 0, 0] },
            { id: 'dome', sku: '6970', projectId: '59', position: [0, 0, 0], maxResolution: { ios: '2K', android: '4K' } },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        coupe_1lt: {
          defaultSkus: [
            { id: 'common_exterior', sku: '6659', projectId: '59', position: [0, 0, 0] },
            { id: 'exterior-color', sku: '6645', projectId: '59', position: [0, 0, 0] },
            { id: 'interior-color', sku: '6674', projectId: '59', position: [0, 0, 0] },
            { id: 'variants_parts', sku: '6648', projectId: '59', position: [0, 0, 0] },
            { id: 'alloy', sku: '6789', projectId: '59', position: [0, 0, 0] },
            { id: 'dome', sku: '6969', projectId: '59', position: [0, 0, 0], maxResolution: { ios: '2K', android: '4K' } },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        coupe_2lt: {
          defaultSkus: [
            { id: 'common_exterior', sku: '6659', projectId: '59', position: [0, 0, 0] },
            // Default paint is no-cost Black (extBlack). Default interior is Jet Black
            // Napa with perforated inserts (intJetBlack, 6738, priced 0) with its default
            // GT2 seat/belt/stitch (see _NAPA_DEFAULT_INT), so the header reads the bare
            // 2LT price (AED 386,200). Jet Black Mulan (6994) stays selectable.
            { id: 'exterior-color', sku: '6701', projectId: '59', position: [0, 0, 0] },
            { id: 'interior-color', sku: '6738', projectId: '59', position: [0, 0, 0] },
            { id: 'variants_parts', sku: '7005', projectId: '59', position: [0, 0, 0] },
            { id: 'alloy', sku: '6789', projectId: '59', position: [0, 0, 0] },
            { id: 'dome', sku: '6969', projectId: '59', position: [0, 0, 0], maxResolution: { ios: '2K', android: '4K' } },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
      },
      backgrounds: {
        // Model-level skus are the Stingray/Corvette ones; GSX variants override via
        // ui-metadata.backgroundVariantSkus (see App background-switch effect).
        racingTrack: {
          isDefault: false,
          sku: '6926',
          project: '59',
        },
        cityLandscape2: {
          isDefault: true,
          sku: '6969',
          project: '59',
        },
      },
      ambientLighting: {
        ambientColors: '',
        ambientLightings: {},
        relevantVariants: [],
      },
      colors: {
        interior: {
          intJetBlack: {
            sku: '6738',
            project: '59',
            relevantVariants: ['coupe_2lt', 'coupe_3lt', 'gsx_coupe_3lz'],
            notCompatibleExteriorColors: [],
          },
          intSkyCoolGray: {
            sku: '6647',
            project: '59',
            relevantVariants: ['coupe_2lt', 'coupe_3lt', 'gsx_coupe_3lz'],
            notCompatibleExteriorColors: [],
          },
          intNatural: {
            sku: '6708',
            project: '59',
            relevantVariants: ['coupe_2lt', 'coupe_3lt', 'gsx_coupe_3lz'],
            notCompatibleExteriorColors: [],
          },
          intAdrenalineRed: {
            sku: '6665',
            project: '59',
            relevantVariants: ['coupe_2lt', 'coupe_3lt', 'gsx_coupe_3lz'],
            notCompatibleExteriorColors: [],
          },
          intSantoriniBlue: {
            sku: '6753',
            project: '59',
            relevantVariants: ['coupe_3lt', 'gsx_coupe_3lz'],
            notCompatibleExteriorColors: [],
          },
          intNaturalDipped: {
            sku: '6643',
            project: '59',
            relevantVariants: ['coupe_3lt', 'gsx_coupe_3lz'],
            notCompatibleExteriorColors: [],
          },
          intAdrenalineRedDipped: {
            sku: '6732',
            project: '59',
            relevantVariants: ['coupe_3lt', 'gsx_coupe_3lz'],
            notCompatibleExteriorColors: [],
          },
          intAsymmetricalAdrRed: {
            sku: '6752',
            project: '59',
            relevantVariants: ['coupe_3lt', 'gsx_coupe_3lz'],
            notCompatibleExteriorColors: [],
          },
          // New coupe-3LT color: Asymmetrical Santorini Blue / Jet Black (Napa, perforated).
          // Has a third seat (Sport GT2); asset messages use the "Adrenaline_Blue" naming.
          intAsymSantoriniJetBlack: {
            sku: '7001',
            project: '59',
            relevantVariants: ['coupe_3lt', 'gsx_coupe_3lz'],
            notCompatibleExteriorColors: [],
          },
          intHabanero: {
            sku: '6684',
            project: '59',
            relevantVariants: ['coupe_3lt', 'gsx_coupe_3lz'],
            notCompatibleExteriorColors: [],
          },
          intVeryDarkAtmosphere: {
            sku: '6725',
            project: '59',
            relevantVariants: ['coupe_3lt', 'gsx_coupe_3lz'],
            notCompatibleExteriorColors: [],
          },
          intUltimateSuedeJetBlack: {
            sku: '6697',
            project: '59',
            relevantVariants: ['coupe_3lt', 'gsx_coupe_3lz'],
            notCompatibleExteriorColors: [],
          },
          ..._convInteriorColors,
          ..._suededInteriorColors,
          // Stingray Coupe-1LT — Mulan/Performance interiors (GT1). The color loads
          // the seat; no GT2/Sport seat toggle. relevantVariants → coupe_1lt only.
          int1ltJetBlackMulan: { sku: '6674', project: '59', relevantVariants: ['coupe_1lt'], notCompatibleExteriorColors: [] },
          int1ltSkyCoolGrayMulan: { sku: '6642', project: '59', relevantVariants: ['coupe_1lt'], notCompatibleExteriorColors: [] },
          int1ltAdrenalineRedMulan: { sku: '6675', project: '59', relevantVariants: ['coupe_1lt'], notCompatibleExteriorColors: [] },
          int1ltJetBlackPerformance: { sku: '6744', project: '59', relevantVariants: ['coupe_1lt'], notCompatibleExteriorColors: [] },
          // Stingray Coupe-2LT Mulan leather interior (distinct 2LT SKUs; seat loads with the colour asset like 1LT).
          int2ltJetBlackMulan: { sku: '6994', project: '59', relevantVariants: ['coupe_2lt'], notCompatibleExteriorColors: [] },
          int2ltSkyCoolGrayMulan: { sku: '6993', project: '59', relevantVariants: ['coupe_2lt'], notCompatibleExteriorColors: [] },
          int2ltAdrenalineRedMulan: { sku: '6999', project: '59', relevantVariants: ['coupe_2lt'], notCompatibleExteriorColors: [] },
          int2ltNaturalMulan: { sku: '7010', project: '59', relevantVariants: ['coupe_2lt'], notCompatibleExteriorColors: [] },
        },
        exterior: {
          extArcticWhite: {
            sku: '6666',
            project: '59',
            relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
          },
          extBlack: {
            sku: '6701',
            project: '59',
            relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
          },
          extTorchRed: {
            sku: '6662',
            project: '59',
            relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
          },
          extCompetitionYellow: {
            sku: '6669',
            project: '59',
            relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
          },
          extSebringOrange: {
            sku: '6716',
            project: '59',
            relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
          },
          extRoswellGreen: {
            sku: '6724',
            project: '59',
            relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
          },
          extBladeSilver: {
            sku: '6739',
            project: '59',
            relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
          },
          extCrimsonVelocityRed: {
            sku: '6645',
            project: '59',
            relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
          },
          extPitchGray: {
            sku: '6997',
            project: '59',
            relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
          },
          extAdmiralBlue: {
            sku: '6990',
            project: '59',
            relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
          },
        },
      },
      features: {
        // === Exterior features (per Corvette mapping sheet, by trim) ===
        // args: _feat(view, trims, cinematic (sheet col 1), shader (sheet col 4))
        // Removable Roof — 1LT / 3LT / GSX Coupe (coupes only; not convertibles)
        featRemovableRoof: _feat('exterior', ['coupe_1lt', 'coupe_2lt', 'coupe_3lt', 'gsx_coupe_3lz'], 'ROOF', 'SHADER_ROOF'),
        // Front lift suspension — 1LT / 3LT / GSX Coupe / GSX Convertible (no 2LT)
        featFrontSuspension: _feat('exterior', ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'], 'Front suspension'),
        // Performance exhaust — 1LT / GSX Coupe / GSX Convertible
        featExhaust: _feat('exterior', ['coupe_1lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'], 'Exhaust', 'SHADER_EXHAUST'),
        // Exterior Design — 1LT / 3LT / GSX Coupe / GSX Convertible (no 2LT)
        featExteriorDesign: _feat('exterior', ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'], 'Exterior Design'),
        // Wide-body aerodynamics — 1LT / 3LT / GSX Coupe / GSX Convertible (no 2LT)
        featWideBody: _feat('exterior', ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'], 'Wide-body'),
        // Automatic leveling headlamps — 1LT / 3LT / GSX Coupe / GSX Convertible (no 2LT)
        featHeadlamps: _feat('exterior', ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'], 'Headlamps', 'SHADER_HEADLAMPS'),

        // === Interior features ===
        // 14-speaker audio — all trims. Close the door while playing so the door panel
        // doesn't block the speaker view. snapToLastFrame => close instantly (no animation).
        featSpeaker: _feat('interior', ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'], 'Speakers', 'SHADER_SPEAKER', { intrDoors: { add: false, snapToLastFrame: true } }),
        // Heated & ventilated seats — all trims
        featHeatedSeats: _feat('interior', ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'], 'Seats'),
        // Head-Up Display — 3LT / 2LT / GSX Coupe / GSX Convertible (no 1LT)
        featHUD: _feat('interior', ['coupe_2lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'], 'HUD', 'SHADER_HUD'),
        // Heated steering wheel — 3LT / 2LT / GSX Coupe / GSX Convertible (no 1LT)
        featSteeringWheel: _feat('interior', ['coupe_2lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'], 'Steering wheel', 'SHADER_STEERING_WHEEL'),
        // Standard GT2 seats with Napa leather — 3LT / 2LT / GSX Coupe / GSX Convertible (no 1LT)
        featGT2Seats: _feat('interior', ['coupe_2lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'], 'Seats'),
        // Custom leather-wrapped instrument panel — 3LT / 2LT / GSX Coupe / GSX Convertible (no 1LT)
        featInstrumentPanel: _feat('interior', ['coupe_2lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'], 'Instrument panel and Door', 'SHADER_INSTRUMENT_PANEL'),
        // Sueded microfiber upper interior trim — 2LT / GSX Coupe / GSX Convertible
        featSuededTrim: _feat('interior', ['conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'], 'Instrument panel and Door'),
        // Custom leather-wrapped instrument panel AND doors — 2LT / GSX Coupe / GSX Convertible
        featInstrumentPanelDoors: _feat('interior', ['conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'], 'Instrument panel and Door'),
      },
      accessories: {
        ..._interiorSubAccessories,
        ..._graphicsAccessories,
        ..._gsAccessories,
        ..._convAccessories,
        ..._baseStateAccessories,
        accVariantParts: {
          view: 'exterior',
          groupId: 'Accessories',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'EXT_Coupe_Stingray_3LT',
              identifiers: ['exterior-color'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_3lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accVariantPartsGsxCoupe: {
          view: 'exterior',
          groupId: 'Accessories',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'EXT_Coupe_GSX',
              identifiers: ['exterior-color'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['gsx_coupe_3lz'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accVariantPartsConv2lt: {
          view: 'exterior',
          groupId: 'Accessories',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'EXT_Convertible_Stingray_2LT', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['conv_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accVariantPartsGsxConv: {
          view: 'exterior',
          groupId: 'Accessories',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'EXT_Convertible_GSX', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['gsx_conv_3lz'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accVariantPartsCoupe1lt: {
          view: 'exterior',
          groupId: 'Accessories',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'EXT_Coupe_Stingray_1LT', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_1lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accVariantPartsCoupe2lt: {
          view: 'exterior',
          groupId: 'Accessories',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'EXT_Coupe_Stingray_2LT', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accCaliperBlack: {
          view: 'exterior',
          groupId: 'Caliper',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Calipers_Black_Stingray',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_CALIPER',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accCaliperVelocityYellow: {
          view: 'exterior',
          groupId: 'Caliper',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Calipers_Velocity_Yellow_Stingray',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_CALIPER',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accCaliperBrightRed: {
          view: 'exterior',
          groupId: 'Caliper',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Calipers_Bright_Red_Stingray',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_CALIPER',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accCaliperEdgeRed: {
          view: 'exterior',
          groupId: 'Caliper',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Calipers_Edge_Red_Stingray',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_CALIPER',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accCaliperBlue: {
          view: 'exterior',
          groupId: 'Caliper',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Calipers_Blue',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_CALIPER',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accRoofStandard: {
          view: 'exterior',
          groupId: 'Roof',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Standard_Roof_Coupe',
              identifiers: ['exterior-color'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ROOF',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_1lt', 'coupe_2lt', 'coupe_3lt', 'gsx_coupe_3lz'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accRoofCarbonFiber: {
          view: 'exterior',
          groupId: 'Roof',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Carbon_Fiber_Roof_Coupe',
              identifiers: ['exterior-color'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ROOF',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_1lt', 'coupe_2lt', 'coupe_3lt', 'gsx_coupe_3lz'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accEngineDefault: {
          view: 'exterior',
          groupId: 'Engine Cover',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Engine_Color_Default',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ENGINE',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accEngineRed: {
          view: 'exterior',
          groupId: 'Engine Cover',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Engine_Color_Red',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ENGINE',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accEngineSilver: {
          view: 'exterior',
          groupId: 'Engine Cover',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Engine_Color_Silver',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ENGINE',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accEngineBlue: {
          view: 'exterior',
          groupId: 'Engine Cover',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Engine_Color_Blue',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ENGINE',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // Exhaust (ALL variants): Quad center-exit exhaust. Performance_engine is the
        // hidden default; the two Quad options (Black / Bright) are selectable. The old
        // Exhaust_Tips_Chrome / Exhaust_Tips_Black tips have been removed — chrome is now
        // covered by the Quad center-exit exhaust. NOTE: these graphics messages must
        // exist for every variant's assets.
        accExhaustPerformance: {
          view: 'exterior',
          groupId: 'Exhaust Tip',
          display: false,
          isDefault: true,
          applyMessages: [{ message: 'Performance_engine', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false }],
          cameraMessages: [{ message: 'CAMERA_EXHAUST_TIP', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false }],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_1lt', 'coupe_2lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accQuadExhaustBlack: {
          view: 'exterior',
          groupId: 'Exhaust Tip',
          display: true,
          isDefault: false,
          applyMessages: [{ message: 'Quad_center_exit_exhaust_Black', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false }],
          cameraMessages: [{ message: 'CAMERA_ENGINE_EXHAUST', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false }],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_1lt', 'coupe_2lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accQuadExhaustBright: {
          view: 'exterior',
          groupId: 'Exhaust Tip',
          display: true,
          isDefault: false,
          applyMessages: [{ message: 'Quad_center_exit_exhaust_Bright', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false }],
          cameraMessages: [{ message: 'CAMERA_ENGINE_EXHAUST', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false }],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_1lt', 'coupe_2lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accOrvmBodyColor: {
          view: 'exterior',
          groupId: 'OrvmPkg',
          display: false,
          isDefault: true,
          // Default: body-color mirrors. Spoiler carbon-flash is cleared app-side when this
          // (the Carbon Flash package being deselected) removes accOrvmCarbonFlash. No shader.
          applyMessages: [
            { message: 'ORVM_Body_Color', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accOrvmCarbonFlash: {
          view: 'exterior',
          groupId: 'OrvmPkg',
          display: true,
          isDefault: false,
          // Carbon Flash Metallic mirrors. The matching spoiler carbon-flash finish
          // (T0A vs Low Profile) is coordinated app-side from accessoriesIDs, because the
          // finish interactions aren't reported by get-if-interaction-active. No SHADER_ORVM.
          applyMessages: [
            { message: 'ORVM_Carbon_Flash', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_SPOILER_SPLITTER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        gsCarbonFlashDefault: {
          view: 'exterior',
          groupId: 'GsCarbonFlashPkg',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'ORVM_Body_Color', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
            { message: 'Spoiler_GS_Body_Color', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [],
          shaderMessages: [
            { message: 'SHADER_ORVM', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['gsx_coupe_3lz', 'gsx_conv_3lz'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        gsCarbonFlashPkg: {
          view: 'exterior',
          groupId: 'GsCarbonFlashPkg',
          display: true,
          isDefault: false,
          applyMessages: [
            { message: 'ORVM_Carbon_Flash', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
            { message: 'Spoiler_GS_Carbon_Flash', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [],
          shaderMessages: [
            { message: 'SHADER_ORVM', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['gsx_coupe_3lz', 'gsx_conv_3lz'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accSpoilerDefault: {
          view: 'exterior',
          groupId: 'SpoilerStingrayPkg',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Default_Front_Splitter_Spoiler_T0A_Z51',
              identifiers: ['exterior-color'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_SPOILER_SPLITTER',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accSpoilerLowProfile: {
          view: 'exterior',
          groupId: 'SpoilerStingrayPkg',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Optional_Low_Profile',
              identifiers: ['exterior-color'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_SPOILER_SPLITTER',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_SPOILER_SPLITTER',
              identifiers: ['exterior-color'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // Stingray Coupe-2LT "Aero delete for Z51 Performance Package": selecting it
        // applies NOTHING and, being in the single-select SpoilerStingrayPkg group,
        // deselects accSpoilerDefault so the T0A splitter/spoiler (6779) is removed.
        accSpoilerAeroDelete2lt: {
          view: 'exterior',
          groupId: 'SpoilerStingrayPkg',
          display: true,
          isDefault: false,
          applyMessages: [],
          cameraMessages: [{ message: 'CAMERA_SPOILER_SPLITTER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false }],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accAlloyQEB: {
          view: 'exterior',
          groupId: 'Alloy Wheels',
          display: true, // Stingray default wheel, listed so it shows as selected on load
          isDefault: false,
          applyMessages: [
            {
              message: 'QEB',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ALLOY',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accAlloyQ99: {
          view: 'exterior',
          groupId: 'Alloy Wheels',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Q99',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ALLOY',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accAlloyQ9A: {
          view: 'exterior',
          groupId: 'Alloy Wheels',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Q9A',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ALLOY',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accAlloyQ9I: {
          view: 'exterior',
          groupId: 'Alloy Wheels',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Q9I',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ALLOY',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accAlloyQ9O: {
          view: 'exterior',
          groupId: 'Alloy Wheels',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Q9O',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ALLOY',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accAlloyQE6: {
          view: 'exterior',
          groupId: 'Alloy Wheels',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'QE6',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ALLOY',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accPkgStealthTrim: {
          view: 'interior',
          groupId: 'InteriorTrim',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Stealth_Interior_Trim_Package_Coupe',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_INT_PACKAGE',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_1lt', 'coupe_2lt', 'coupe_3lt', 'gsx_coupe_3lz'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accPkgCarbonFiberTrim: {
          view: 'interior',
          groupId: 'InteriorTrim',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Carbon_Fiber_Interior_Trim_Coupe',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_INT_PACKAGE',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_1lt', 'coupe_2lt', 'coupe_3lt', 'gsx_coupe_3lz'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accPkgEngineAppearance: {
          view: 'exterior',
          groupId: 'EngineAppearancePkg',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Coupe_Engine_Appearance_Optional',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ENGINE',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_1lt', 'coupe_2lt', 'coupe_3lt', 'gsx_coupe_3lz'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accBadgeBlack: {
          view: 'exterior',
          groupId: 'Logo',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'StingRay_Coupe_Logo_Black',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_BADGE',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_1lt', 'coupe_2lt', 'coupe_3lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accBadgeChrome: {
          view: 'exterior',
          groupId: 'Logo',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'StingRay_Coupe_Logo_Chrome',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_BADGE',
              identifiers: ['common_exterior'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_1lt', 'coupe_2lt', 'coupe_3lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accBodyColorAccent: {
          view: 'exterior',
          groupId: 'BodyAccentPkg',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'StingRay_Exterior_Accent_Body_Color_Optional',
              identifiers: ['exterior-color'],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            { message: 'CAMERA_SPOILER_SPLITTER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
      },
      packages: {},
      interactions: {
        intrDoors: {
          type: 'basic',
          view: ['exterior', 'interior'],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                lowerRadiusLimit: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 5.1023,
                beta: 1.3902,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: ['coupe_1lt', 'coupe_2lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
        },
        intrBoot: {
          type: 'basic',
          view: ['exterior'],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                lowerRadiusLimit: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: { type: 'interior', messages: {} },
            interiorCamera_1: { type: 'interior', messages: {} },
            interiorCamera_2: { type: 'interior', messages: {} },
          },
          relevantVariants: ['coupe_1lt', 'coupe_2lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
        },
        intrRoof: {
          type: 'basic',
          view: ['exterior'],
          startMessages: [
            {
              message: 'ROOF_OPEN',
              identifiers: ['common_exterior', 'exterior-color', 'interior-color', 'variants_parts', 'alloy', 'dome'],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'ROOF_CLOSE',
              identifiers: ['common_exterior', 'exterior-color', 'interior-color', 'variants_parts', 'alloy', 'dome'],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 2.5156,
                beta: 1.4355,
                radius: 8,
                lowerRadiusLimit: 8,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: { type: 'interior', messages: {} },
            interiorCamera_1: { type: 'interior', messages: {} },
            interiorCamera_2: { type: 'interior', messages: {} },
          },
          relevantVariants: ['conv_2lt', 'gsx_conv_3lz'],
        },
        intrHeadlight: {
          type: 'basic-light',
          view: ['exterior'],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                lowerRadiusLimit: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: { type: 'interior', messages: {} },
            interiorCamera_1: { type: 'interior', messages: {} },
            interiorCamera_2: { type: 'interior', messages: {} },
          },
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
        },
        intrDRL: {
          type: 'basic-light',
          view: ['exterior'],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                lowerRadiusLimit: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: { type: 'interior', messages: {} },
            interiorCamera_1: { type: 'interior', messages: {} },
            interiorCamera_2: { type: 'interior', messages: {} },
          },
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
        },
        intrTaillight: {
          type: 'basic-light',
          view: ['exterior'],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: ['exterior-color', 'common_exterior', 'common_interior', 'interior-color', 'variants_parts'],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: ['exterior-color', 'common_exterior', 'common_interior', 'interior-color', 'variants_parts'],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7054,
                beta: 1.4258,
                radius: 5,
                lowerRadiusLimit: 5,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: { type: 'interior', messages: {} },
            interiorCamera_1: { type: 'interior', messages: {} },
            interiorCamera_2: { type: 'interior', messages: {} },
          },
          relevantVariants: ['coupe_2lt', 'coupe_1lt', 'coupe_3lt', 'conv_2lt', 'gsx_coupe_3lz', 'gsx_conv_3lz'],
        },
      },
      hotspots: {
        exteriorCamera: {
          type: 'exterior',
          messages: {
            fov: 0.4,
            // Tight near plane so the car isn't clipped at the closest orbit radius.
            nearplane: 0.03,
            alpha: 2.02,
            beta: 1.47,
            radius: 8,
            upperRadiusLimit: 9,
            lowerRadiusLimit: 6,
            lowerBetaLimit: 0.1,
            upperBetaLimit: 1.6,
            lowerAlphaLimit: null,
            upperAlphaLimit: null,
            target: {
              x: 0,
              y: 0.65,
              z: 0,
            },
            isFovZoomModeOn: false,
          },
        },
        interiorCamera: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA',
            identifiers: ['common_exterior'],
            lowerBetaLimit:0.05,
            upperBetaLimit: 2.7
          },
        },
        interiorCamera_1: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA_2',
            identifiers: ['common_exterior'],
          },
        },
        interiorCamera_2: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA_2',
            identifiers: ['common_exterior'],
          },
        },
      },
      usdzPathWithVariable: '',
    },
  },
};
